import { Muxer, ArrayBufferTarget } from 'mp4-muxer';
import { GIFEncoder, quantize, applyPalette } from 'gifenc';
import type { CaptureClip } from '../components/LivingSculpt.js';

/**
 * Motion-clip exporter — turns the living-sculpt's offscreen orbit capture into a
 * downloadable file, entirely in the browser (no server/provider spend). Prefers
 * an HD H.264 MP4 via WebCodecs (crisp gold, tiny file, plays everywhere); falls
 * back to an animated GIF where WebCodecs isn't available (older Safari). The
 * capture renders an opaque vignette backdrop AND (free tier) a 3D gold-foil
 * "Gilding.app" mark into the scene, so frames go straight to the encoder — no 2D
 * compositing. HD is clean and larger.
 */

export interface ClipResult {
  blob: Blob;
  ext: 'mp4' | 'gif';
}

const FPS = 30;
// One full orbit over exactly the LIVE ambient period (LivingSculpt: 6.2s). Was 138
// (4.6s) — stale from when the ambient period was 4.6s; it was retuned to 6.2s but the
// clip length wasn't, so downloads orbited FASTER than the on-screen sculpt (user
// 2026-07-05). 186 = 6.2s × 30fps. ph = i/FRAMES·2π ⇒ frame 0 == frame FRAMES, so the
// loop is geometrically seamless.
const FRAMES = 186; // 6.2s at 30fps — matches the live ambient orbit period exactly
// NORMALIZED drive amplitudes: 1.0 = the full live ambient orbit. The sculpt maps
// drive → camera-orbit world amps (ORBIT_AMP_X/Y = 0.25, 0.15) on spatial gens or
// the whole-card tilt on flat gens, and applies ORBIT_SCALE (aspect damping) inside
// the capture loop — the capture calls the SAME applyPose as the live/ambient render
// (LivingSculpt captureClip), so the exported clip matches the live preview exactly:
// same depth, same edge-pin, same motion. No separate render path to drift out of sync.
const AMP_X = 1;
const AMP_Y = 1;
// GIF holds every frame as RAW pixels in memory before encoding, so frame count ×
// size is a hard memory budget (phones crash in the hundreds of MB). We cap the
// frame count and, crucially, time the loop by a FIXED DURATION rather than a
// fixed fps: the old code ran the whole orbit in ~2.4s (18fps × 44), which was
// ~2.5× faster than the on-screen 6.2s orbit and read as "way too fast/glitchy".
// Driving the per-frame delay from GIF_LOOP_MS keeps the motion graceful and the
// same speed no matter how many frames a given device can afford.
const GIF_FRAMES = 54; // max frames; memory-capped per device in renderGif
const GIF_LOOP_MS = 5000; // total loop duration — a calm orbit, close to the live feel

type Ctx2D = CanvasRenderingContext2D;

function evenDims(frameAspect: number, longSide: number): { w: number; h: number } {
  let w: number;
  let h: number;
  if (frameAspect >= 1) {
    w = longSide;
    h = Math.round(longSide / frameAspect);
  } else {
    h = longSide;
    w = Math.round(longSide * frameAspect);
  }
  return { w: w - (w % 2), h: h - (h % 2) }; // H.264 needs even dimensions
}

/** Size by the SHORT edge, so "720p" means 720 on the short side at EVERY aspect
 *  — a portrait 9:16 clip becomes 720×1280, not 428×760. Sizing by the long edge
 *  made portrait/landscape clips far below their labelled resolution (only square
 *  was close), which read as blurry on a phone that upscales them. */
function evenDimsShort(frameAspect: number, shortSide: number): { w: number; h: number } {
  let w: number;
  let h: number;
  if (frameAspect >= 1) {
    h = shortSide;
    w = Math.round(shortSide * frameAspect);
  } else {
    w = shortSide;
    h = Math.round(shortSide / frameAspect);
  }
  return { w: w - (w % 2), h: h - (h % 2) };
}

/** MP4 output size. HD by the long edge (1920 → 1080p+); the free tier by the
 *  short edge (720 → honest 720p at every aspect). Shared so the codec probe
 *  and the encoder always size the frame identically. Exported for testing. */
export function clipDims(frameAspect: number, hd: boolean): { w: number; h: number } {
  return hd ? evenDims(frameAspect, 1920) : evenDimsShort(frameAspect, 720);
}

/** Lay one captured (already opaque, already watermarked-in-3D) RGBA frame onto
 *  the output context. The brand mark is baked into the scene now, so there's no
 *  2D compositing to do beyond this blit. */
function compose(octx: Ctx2D, rgba: Uint8ClampedArray, w: number, h: number): void {
  const id = octx.createImageData(w, h);
  id.data.set(rgba);
  octx.putImageData(id, 0, 0);
}

function makeCanvas(w: number, h: number): { el: HTMLCanvasElement; ctx: Ctx2D } {
  const el = document.createElement('canvas');
  el.width = w;
  el.height = h;
  const ctx = el.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  return { el, ctx };
}

// ---- MP4 colour metadata (`colr` nclx box) ------------------------------------
// Hardware H.264 encoders (esp. on phones) emit limited-range BT.709 with NO
// colour tags. Players then guess — usually treating limited-range data as full,
// so blacks lift and the whole clip looks milky/faded. We stamp a `colr` box
// describing exactly what the encoder produced so every player renders true
// colour + contrast. (Software encoders happen to round-trip fine, which is why
// it only shows on device.)
const PRIMARIES: Record<string, number> = { bt709: 1, bt470bg: 5, smpte170m: 6, bt2020: 9 };
const TRANSFER: Record<string, number> = {
  bt709: 1,
  smpte170m: 6,
  'iec61966-2-1': 13,
  linear: 8,
  pq: 16,
  hlg: 18,
};
const MATRIX: Record<string, number> = { rgb: 0, bt709: 1, bt470bg: 5, smpte170m: 6, 'bt2020-ncl': 9 };

function u32(b: Uint8Array, o: number): number {
  return ((b[o]! << 24) | (b[o + 1]! << 16) | (b[o + 2]! << 8) | b[o + 3]!) >>> 0;
}
function setU32(b: Uint8Array, o: number, v: number): void {
  b[o] = (v >>> 24) & 255;
  b[o + 1] = (v >>> 16) & 255;
  b[o + 2] = (v >>> 8) & 255;
  b[o + 3] = v & 255;
}
function boxType(b: Uint8Array, o: number): string {
  return String.fromCharCode(b[o + 4]!, b[o + 5]!, b[o + 6]!, b[o + 7]!);
}
/** First child box of `type` within [start,end). */
function findChild(b: Uint8Array, start: number, end: number, type: string) {
  let o = start;
  while (o + 8 <= end) {
    const size = u32(b, o);
    if (size < 8 || o + size > end) break;
    if (boxType(b, o) === type) return { offset: o, size, content: o + 8, endo: o + size };
    o += size;
  }
  return null;
}

function buildColr(cs?: VideoColorSpaceInit | null): Uint8Array {
  const prim = (cs?.primaries && PRIMARIES[cs.primaries]) || 1; // BT.709
  const trans = (cs?.transfer && TRANSFER[cs.transfer]) || 1;
  const mat = cs?.matrix != null ? (MATRIX[cs.matrix] ?? 1) : 1;
  const full = cs?.fullRange ? 0x80 : 0x00;
  const box = new Uint8Array(19);
  setU32(box, 0, 19);
  box.set([0x63, 0x6f, 0x6c, 0x72], 4); // 'colr'
  box.set([0x6e, 0x63, 0x6c, 0x78], 8); // 'nclx'
  box[12] = (prim >> 8) & 255;
  box[13] = prim & 255;
  box[14] = (trans >> 8) & 255;
  box[15] = trans & 255;
  box[16] = (mat >> 8) & 255;
  box[17] = mat & 255;
  box[18] = full;
  return box;
}

/** Insert a `colr` box into the video sample entry. Requires the moov to be AFTER
 *  mdat (`fastStart: false`) so growing it doesn't shift any media offsets. */
function injectColr(buffer: ArrayBuffer, cs?: VideoColorSpaceInit | null): ArrayBuffer {
  const b = new Uint8Array(buffer);
  const moov = findChild(b, 0, b.length, 'moov');
  if (!moov) return buffer;
  const trak = findChild(b, moov.content, moov.endo, 'trak');
  const mdia = trak && findChild(b, trak.content, trak.endo, 'mdia');
  const minf = mdia && findChild(b, mdia.content, mdia.endo, 'minf');
  const stbl = minf && findChild(b, minf.content, minf.endo, 'stbl');
  const stsd = stbl && findChild(b, stbl.content, stbl.endo, 'stsd');
  if (!trak || !mdia || !minf || !stbl || !stsd) return buffer;
  const entries = stsd.content + 8; // skip version/flags(4) + entry_count(4)
  let vse = null;
  for (const t of ['avc1', 'avc3', 'hev1', 'hvc1']) {
    vse = findChild(b, entries, stsd.endo, t);
    if (vse) break;
  }
  if (!vse) return buffer;
  if (findChild(b, vse.content + 78, vse.endo, 'colr')) return buffer; // already tagged
  const avcc = findChild(b, vse.content + 78, vse.endo, 'avcC') ?? findChild(b, vse.content + 78, vse.endo, 'hvcC');
  const insertAt = avcc ? avcc.endo : vse.content + 78;
  const colr = buildColr(cs);
  const out = new Uint8Array(b.length + colr.length);
  out.set(b.subarray(0, insertAt), 0);
  out.set(colr, insertAt);
  out.set(b.subarray(insertAt), insertAt + colr.length);
  for (const box of [moov, trak, mdia, minf, stbl, stsd, vse]) {
    setU32(out, box.offset, u32(out, box.offset) + colr.length);
  }
  return out.buffer;
}

// H.264 levels, with the max frame size each can hold (in macroblocks). A
// codec string's last byte IS the level (avc1.PPCCLL), and a level that can't
// hold the frame is rejected outright — Level 4.0 tops out at 8192 MBs, so a
// square 1920×1920 clip (14400 MBs) fails on EVERY 4.0 string and used to fall
// silently through to GIF. Square is the default aspect, so that was most HD
// exports. Pick the lowest level that actually fits.
const AVC_LEVELS: { hex: string; maxMbs: number }[] = [
  { hex: '28', maxMbs: 8192 }, // 4.0
  { hex: '2A', maxMbs: 8704 }, // 4.2
  { hex: '32', maxMbs: 22080 }, // 5.0
  { hex: '33', maxMbs: 36864 }, // 5.1
  { hex: '34', maxMbs: 36864 }, // 5.2
];

/**
 * Codec strings worth trying for a frame of this size, best-first: High →
 * Main → Baseline at each level that can actually hold the frame, lowest
 * fitting level first (widest player compatibility for the size at hand).
 * Exported for testing.
 */
export function avcCandidates(width: number, height: number): string[] {
  const mbs = Math.ceil(width / 16) * Math.ceil(height / 16);
  return AVC_LEVELS.filter((l) => l.maxMbs >= mbs).flatMap((level) =>
    ['6400', '4D00', '42E0'].map((profile) => `avc1.${profile}${level.hex}`),
  );
}

async function pickAvcCodec(width: number, height: number): Promise<string | null> {
  const VE = (globalThis as { VideoEncoder?: typeof VideoEncoder }).VideoEncoder;
  if (!VE || typeof VE.isConfigSupported !== 'function') return null;
  for (const codec of avcCandidates(width, height)) {
    try {
      const res = await VE.isConfigSupported({ codec, width, height, bitrate: 4_000_000, framerate: FPS });
      if (res.supported) return codec;
    } catch {
      /* try next */
    }
  }
  return null;
}

/** Make sure the display serif is ready so the 3D mark renders in-brand. */
async function ensureFont(): Promise<void> {
  try {
    if ('fonts' in document) {
      await Promise.all([document.fonts.load('700 96px Fraunces'), document.fonts.load('600 96px Fraunces')]);
    }
  } catch {
    /* fall back to Georgia serif */
  }
}

async function renderMp4(
  capture: CaptureClip,
  frameAspect: number,
  hd: boolean,
  codec: string,
  onProgress?: (p: number) => void,
): Promise<ClipResult> {
  // Bitrate auto-scales with pixel count below (capped at 40 Mbps). Size is
  // shared with the codec probe via clipDims (HD 1080p+, free honest 720p).
  const { w, h } = clipDims(frameAspect, hd);
  const out = makeCanvas(w, h);
  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    video: { codec: 'avc', width: w, height: h, frameRate: FPS },
    // moov AFTER mdat so we can stamp the colour box without shifting media offsets.
    fastStart: false,
  });
  let colorSpace: VideoColorSpaceInit | null = null;
  const encoder = new VideoEncoder({
    output: (chunk, meta) => {
      // The encoder reports the colour space it actually produced — capture it so
      // the `colr` tag we add matches the device's own hardware encoder exactly.
      if (!colorSpace && meta?.decoderConfig?.colorSpace) colorSpace = meta.decoderConfig.colorSpace;
      muxer.addVideoChunk(chunk, meta);
    },
    error: (e) => console.error('VideoEncoder error', e),
  });
  // Bitrate is the DOMINANT sharpness lever for the export: H.264 low-passes fine
  // detail and smears the moving vignette + particles when starved, which reads as
  // an overall "blurry" clip even at 1080p. Bumped 0.19→0.34 bpp and the cap
  // 19→40 Mbps for a large jump in crispness (a 6s 1080p loop lands ~13-16 MB —
  // fine for a premium download). `latencyMode: 'quality'` tells the encoder to use
  // full rate-distortion lookahead instead of the low-latency fast path.
  const bitrate = Math.min(40_000_000, Math.round(w * h * FPS * 0.34));
  // bitrateMode: WebKit-only 'constant'. iOS Safari's VideoToolbox encoder treats
  // a VBR target as a suggestion and undershoots hard during motion peaks (the
  // orbit's pixel velocity maxes at the quarter/three-quarter marks), which reads
  // as the clip "blurring midway through" on phone exports; CBR pins the rate
  // through the swing. But Chrome's OpenH264 rate controller does the OPPOSITE
  // failure under CBR here: it over-commits on the first ~4s of I-frames (12 forced
  // keyframes at 500KB each ≈ 25Mbps burst against a 20Mbps ceiling), exhausts its
  // virtual buffer, then emits 16KB stub I-frames mid-clip until the budget refills —
  // that's the "sharp → flash of blocks → blurred rest" bug the user saw. Gate CBR
  // to WebKit only; let Chrome's VBR spread the bits sensibly across the orbit.
  encoder.configure({
    codec,
    width: w,
    height: h,
    bitrate,
    bitrateMode: isWebKit() ? 'constant' : 'variable',
    framerate: FPS,
    latencyMode: 'quality',
  });

  await capture({
    width: w,
    height: h,
    frames: FRAMES,
    ampX: AMP_X,
    ampY: AMP_Y,
    watermark: !hd,
    onFrame: async (rgba, _w, _h, i) => {
      compose(out.ctx, rgba, w, h);
      const frame = new VideoFrame(out.el, {
        timestamp: Math.round((i * 1e6) / FPS),
        duration: Math.round(1e6 / FPS),
      });
      // Keyframe every 0.5s for uniform quality, PLUS a keyframe on the final frame so
      // both sides of the loop seam are crisp. But SUPPRESS the periodic keyframe when
      // it lands within 8 frames of the end (e.g. 180 vs the forced 185): two big intra
      // frames back-to-back made the rate controller crush the second into a blur — the
      // blurry last frame the user saw once 2× supersampling sharpened the content.
      const keyFrame = i === FRAMES - 1 || (i % 15 === 0 && FRAMES - 1 - i >= 8);
      encoder.encode(frame, { keyFrame });
      frame.close();
      while (encoder.encodeQueueSize > 8) await new Promise((r) => setTimeout(r, 0));
      onProgress?.(((i + 1) / FRAMES) * 0.96);
    },
  });
  await encoder.flush();
  muxer.finalize();
  onProgress?.(1);
  // Stamp the colour metadata so players render true contrast (fixes the milky/
  // faded look on devices whose encoder omits it).
  const tagged = injectColr(muxer.target.buffer, colorSpace);
  return { blob: new Blob([tagged], { type: 'video/mp4' }), ext: 'mp4' };
}

async function renderGif(
  capture: CaptureClip,
  frameAspect: number,
  hd: boolean,
  onProgress?: (p: number) => void,
): Promise<ClipResult> {
  // GIF holds every frame as RAW RGBA until encode, so resolution × frame count is
  // a hard memory budget. The old 540px cap looked heavily blurred as a profile
  // picture; we raise the long side a lot and derive a SAFE frame count from a
  // per-device memory budget — desktops keep the full-length loop at high res,
  // phones trade a touch of length (never below 24 frames) to stay crash-safe.
  const nav = navigator as Navigator & { deviceMemory?: number };
  const constrained =
    (typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches) ||
    (typeof nav.deviceMemory === 'number' && nav.deviceMemory < 8);
  const longSide = hd ? (constrained ? 720 : 900) : constrained ? 600 : 760;
  const { w, h } = evenDims(frameAspect, longSide);
  const memBudget = constrained ? 90_000_000 : 180_000_000; // raw-frame RAM ceiling
  const frameCount = Math.max(24, Math.min(GIF_FRAMES, Math.floor(memBudget / (w * h * 4))));
  const out = makeCanvas(w, h);
  const frames: Uint8ClampedArray[] = [];
  await capture({
    width: w,
    height: h,
    frames: frameCount,
    ampX: AMP_X,
    ampY: AMP_Y,
    watermark: !hd,
    onFrame: (rgba, _w, _h, i) => {
      compose(out.ctx, rgba, w, h);
      frames.push(new Uint8ClampedArray(out.ctx.getImageData(0, 0, w, h).data));
      onProgress?.(((i + 1) / frameCount) * 0.55);
    },
  });
  // PER-FRAME palette: each frame gets its own optimal 256 colours. A single
  // global palette made colours pop and band as the relight shifts across the
  // orbit (the "glitchy" look); a local table per frame is far cleaner. Delay is
  // derived from the fixed loop duration so the motion is calm and correctly
  // paced regardless of how many frames this device rendered.
  const gif = GIFEncoder();
  const delay = Math.max(20, Math.round(GIF_LOOP_MS / frames.length));
  frames.forEach((f, i) => {
    const palette = quantize(f, 256, { format: 'rgb565' });
    gif.writeFrame(applyPalette(f, palette, 'rgb565'), w, h, { palette, delay });
    onProgress?.(0.55 + ((i + 1) / frames.length) * 0.45);
  });
  gif.finish();
  return { blob: new Blob([gif.bytes() as BlobPart], { type: 'image/gif' }), ext: 'gif' };
}

export async function renderMotionClip(opts: {
  capture: CaptureClip;
  frameAspect: number;
  /** HD tier: larger and no watermark. Free tier: smaller + gold wordmark. */
  hd: boolean;
  /**
   * 'video' (MP4, default) is best for posting. 'gif' forces the GIF path, which
   * is what works as a PROFILE PICTURE on OpenSea / X / Discord (they don't accept
   * mp4 avatars). GIF is bigger + 256-colour, so it renders at a smaller size.
   */
  format?: 'video' | 'gif';
  onProgress?: (p: number) => void;
}): Promise<ClipResult> {
  const { capture, frameAspect, hd, format = 'video', onProgress } = opts;
  if (!hd) await ensureFont();
  if (format === 'gif') {
    return renderGif(capture, frameAspect, hd, onProgress);
  }
  const { w, h } = clipDims(frameAspect, hd);
  const codec = await pickAvcCodec(w, h);
  return codec
    ? renderMp4(capture, frameAspect, hd, codec, onProgress)
    : renderGif(capture, frameAspect, hd, onProgress);
}

type SaveCapableWindow = Window & {
  showSaveFilePicker?: (opts?: {
    suggestedName?: string;
    types?: { description?: string; accept: Record<string, string[]> }[];
  }) => Promise<{
    createWritable: () => Promise<{ write: (data: Blob) => Promise<void>; close: () => Promise<void> }>;
  }>;
};

const isAbort = (e: unknown): boolean =>
  !!e && typeof e === 'object' && 'name' in e && (e as { name?: unknown }).name === 'AbortError';

/** iOS/iPadOS WebKit. There, `<a download>` for a blob does NOT save — it opens
 *  the file inline (plays the video), so a download fallback silently fails. */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iP(hone|ad|od)/.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua));
}

/** Any WebKit-backed browser (iOS-anything, or macOS Safari) — i.e. Apple's
 *  VideoToolbox H.264 encoder underneath. Used to gate encoder settings that
 *  work on VideoToolbox but hurt Chrome's OpenH264 rate controller. */
function isWebKit(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (isIOS()) return true;
  const ua = navigator.userAgent || '';
  return /Safari/.test(ua) && !/Chrome|CriOS|Chromium|Edg|Android|FxiOS/.test(ua);
}

/**
 * The outcome of a save attempt, so the UI can be HONEST (the old code marked
 * every attempt "saved", even when nothing was written):
 *  - 'shared'     — handed to the native share sheet (Save to Photos / share to X)
 *  - 'saved'      — written via the native save dialog (desktop Chromium)
 *  - 'downloaded' — anchor download that reliably saves (desktop, Android)
 *  - 'aborted'    — the user dismissed the share/save dialog
 *  - 'manual'     — this browser can't save programmatically (iOS in-app
 *                   browsers): the caller must show the clip inline with
 *                   press-and-hold / open-in-Safari guidance.
 */
export type SaveOutcome = 'shared' | 'saved' | 'downloaded' | 'aborted' | 'manual';

/** Touch device — used to order the two save actions (share-first on phones,
 *  download-first on desktop). */
export function isTouchDevice(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches
  );
}

function mimeFor(filename: string): string {
  return filename.split('.').pop() === 'gif' ? 'image/gif' : 'video/mp4';
}

/**
 * DOWNLOAD the clip — a real save of the FILE (not a share sheet), so it lands
 * in Downloads (Android) or wherever the save dialog points (desktop). This is
 * the reliable "keep it" action on every platform except iOS, where WebKit
 * plays a blob download inline instead of saving — there we return 'manual' so
 * the UI can show the clip for a press-and-hold / open-in-Safari save.
 */
export async function downloadClip(blob: Blob, filename: string): Promise<SaveOutcome> {
  const ext = filename.split('.').pop() ?? 'mp4';
  const mime = mimeFor(filename);

  const w = window as SaveCapableWindow;
  if (typeof w.showSaveFilePicker === 'function') {
    try {
      const handle = await w.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: 'Motion clip', accept: { [mime]: [`.${ext}`] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return 'saved';
    } catch (e) {
      if (isAbort(e)) return 'aborted';
    }
  }

  if (isIOS()) return 'manual';

  // Desktop / Android — force a download (not inline playback) via octet-stream.
  // On Android this lands in the Downloads folder (findable in Files/Gallery).
  const dl = new Blob([blob], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(dl);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 8000);
  return 'downloaded';
}

/** True if this browser can share a FILE via the native share sheet. */
export function canShareClip(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function'
  );
}

/**
 * SHARE the clip via the native share sheet (post to X, send in Messages, or —
 * on iOS — "Save Video" to Photos). Separate from download so social sharing
 * and keeping-the-file are distinct actions.
 */
export async function shareClip(
  blob: Blob,
  filename: string,
): Promise<'shared' | 'aborted' | 'unsupported'> {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return 'unsupported';
  const file = new File([blob], filename, { type: mimeFor(filename) });
  if (typeof navigator.canShare === 'function' && !navigator.canShare({ files: [file] })) {
    return 'unsupported';
  }
  try {
    await navigator.share({ files: [file], title: 'My gilding' });
    return 'shared';
  } catch (e) {
    return isAbort(e) ? 'aborted' : 'unsupported';
  }
}
